import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Progress,
  Radio,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Typography,
} from "antd";
import {
  MailOutlined,
  SendOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import dayjs from "dayjs";
import {
  useGetMarketingAudienceSummary,
  useSearchMarketingRecipients,
  useSendMarketingEmail,
} from "../services/marketing.service";

const { Title, Text } = Typography;

type RecipientSegment = "all" | "users" | "attendants" | "single";

type CampaignRecord = {
  key: string;
  name: string;
  segment: string;
  sentAt: string;
  delivered: number;
  opened: number | null;
  clicked: number | null;
  status: string;
};

const templateOptions = [
  { label: "Blank Template", value: "blank" },
  { label: "Promo Offer", value: "promo" },
  { label: "Product Update", value: "product" },
  { label: "Retention Winback", value: "retention" },
];

const audienceFilterOptions = [
  { value: "incomplete-signup", label: "Incomplete Signup (Drafts)" },
  { value: "incomplete-attendant-application", label: "Incomplete Attendant Application" },
  { value: "new-today", label: "New Users - Today" },
  { value: "new-this-week", label: "New Users - This Week" },
  { value: "new-last-week", label: "New Users - Last Week" },
  { value: "inactive-attendants", label: "Inactive Attendants" },
  { value: "deactivated-users", label: "Deactivated / Terminated Accounts" },
];

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link", "image"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "link",
  "image",
  "color",
  "background",
  "align",
];

const MarketingDashboard = () => {
  const [form] = Form.useForm();
  const [recipientSegment, setRecipientSegment] = useState<RecipientSegment>("all");
  const [campaigns, setCampaigns] = useState<CampaignRecord[]>([]);
  const [audienceSummary, setAudienceSummary] = useState({ usersCount: 0, attendantsCount: 0, totalCount: 0 });
  const [recipientOptions, setRecipientOptions] = useState<{ value: string; label: string }[]>([]);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const watchedSubject = Form.useWatch("subject", form) ?? "";
  const watchedBody = Form.useWatch("body", form) ?? "";
  const watchedCampaignName = Form.useWatch("campaignName", form) ?? "";
  const watchedSingleRecipientEmail = Form.useWatch("singleRecipientEmail", form) ?? "";

  const audienceSummaryMutation = useGetMarketingAudienceSummary({
    onSuccess: (response: any) => {
      if (response?.statusCode === 200 && response?.data) {
        setAudienceSummary({
          usersCount: response.data.usersCount ?? 0,
          attendantsCount: response.data.attendantsCount ?? 0,
          totalCount: response.data.totalCount ?? 0,
        });
      }
    },
    onError: () => {
      message.error("Failed to load audience counts.");
    },
  });

  useEffect(() => {
    audienceSummaryMutation.mutate();
  }, []);

  const searchRecipientsMutation = useSearchMarketingRecipients({
    onSuccess: (response: any) => {
      if (response?.statusCode === 200 && Array.isArray(response?.data)) {
        setRecipientOptions(
          response.data.map((r: any) => ({
            value: r.email,
            label: `${r.fullName || r.email} (${r.roleName}) - ${r.email}`,
          }))
        );
      }
    },
  });

  const onSearchRecipients = (value: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (!value || value.trim().length < 2) {
      setRecipientOptions([]);
      return;
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchRecipientsMutation.mutate(value.trim());
    }, 300);
  };

  const sendMarketingEmailMutation = useSendMarketingEmail({
    onSuccess: (response: any) => {
      if (response?.statusCode === 200) {
        const sentCount = response?.data?.sentCount ?? 0;
        const failedRecipients: string[] = response?.data?.failedRecipients ?? [];
        setCampaigns((prev) => [
          {
            key: `${Date.now()}`,
            name: (response?.data?.campaignName ?? watchedCampaignName) || "Campaign",
            segment: getSegmentLabel(recipientSegment),
            sentAt: dayjs().format("YYYY-MM-DD HH:mm"),
            delivered: sentCount,
            opened: null,
            clicked: null,
            status: failedRecipients.length > 0 ? "Partially Failed" : "Completed",
          },
          ...prev,
        ]);

        message.success(`Campaign sent. ${sentCount} emails delivered.`);
      } else {
        message.error(response?.statusMessage ?? "Failed to send campaign email.");
      }
    },
    onError: (error: any) => {
      message.error(error?.message ?? "Failed to send campaign email.");
    },
  });

  const onSendCampaign = async (isTestSend: boolean = false) => {
    try {
      const values = await form.validateFields();

      const testRecipientEmail = isTestSend
        ? (values.testRecipientEmail ?? "").trim()
        : null;

      if (isTestSend && !testRecipientEmail) {
        message.error("Please enter your email in Test Recipient Email.");
        return;
      }

      sendMarketingEmailMutation.mutate({
        campaignName: values.campaignName,
        recipientSegment,
        subject: values.subject,
        body: values.body,
        audienceFilters: recipientSegment === "single" ? undefined : values.audienceFilters,
        singleRecipientEmail: recipientSegment === "single" ? values.singleRecipientEmail : undefined,
        testRecipientEmail,
      });
    } catch {
      // Form validation errors are already surfaced by Ant Design.
    }
  };

  const estimatedReach = useMemo(() => {
    if (recipientSegment === "single") return watchedSingleRecipientEmail ? 1 : 0;
    if (recipientSegment === "users") return audienceSummary.usersCount;
    if (recipientSegment === "attendants") return audienceSummary.attendantsCount;
    return audienceSummary.totalCount;
  }, [recipientSegment, audienceSummary, watchedSingleRecipientEmail]);

  const averageOpenRate = useMemo(() => {
    const withOpenData = campaigns.filter((x) => x.opened !== null && x.delivered > 0);
    if (!withOpenData.length) return 0;
    const totalRate = withOpenData.reduce((sum, item) => sum + ((item.opened ?? 0) / item.delivered) * 100, 0);
    return Number((totalRate / withOpenData.length).toFixed(1));
  }, [campaigns]);

  const averageCtr = useMemo(() => {
    const withClickData = campaigns.filter((x) => x.clicked !== null && x.delivered > 0);
    if (!withClickData.length) return 0;
    const totalRate = withClickData.reduce((sum, item) => sum + ((item.clicked ?? 0) / item.delivered) * 100, 0);
    return Number((totalRate / withClickData.length).toFixed(1));
  }, [campaigns]);

  const deliveryReadiness = useMemo(() => {
    const checks = [
      Boolean(watchedCampaignName.trim()),
      Boolean(watchedSubject.trim()),
      Boolean((watchedBody || "").replace(/<[^>]*>/g, "").trim()),
      estimatedReach > 0,
    ];
    const completed = checks.filter(Boolean).length;
    return Math.round((completed / checks.length) * 100);
  }, [watchedCampaignName, watchedSubject, watchedBody, estimatedReach]);

  const getRateDisplay = (value: number | null, delivered: number) => {
    if (value === null || delivered <= 0) return "-";
    return `${Math.round((value / delivered) * 100)}%`;
  };

  const columns = [
    {
      title: "Campaign",
      dataIndex: "name",
      key: "name",
      render: (value: string) => <Text strong>{value}</Text>,
    },
    {
      title: "Audience",
      dataIndex: "segment",
      key: "segment",
    },
    {
      title: "Sent",
      dataIndex: "sentAt",
      key: "sentAt",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value: string) => (
        <span>{value}</span>
      ),
    },
    {
      title: "Open Rate",
      key: "openRate",
      render: (_: unknown, row: any) => {
        return <Text>{getRateDisplay(row.opened, row.delivered)}</Text>;
      },
    },
    {
      title: "CTR",
      key: "clickRate",
      render: (_: unknown, row: any) => {
        return <Text>{getRateDisplay(row.clicked, row.delivered)}</Text>;
      },
    },
  ];

  function getSegmentLabel(segment: RecipientSegment): string {
    if (segment === "users") return "Users";
    if (segment === "attendants") return "Attendants";
    if (segment === "single") return "Single User";
    return "Users + Attendants";
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 0 }}>
            Marketing
          </Title>
          <Text type="secondary">
            Build and send campaigns for users and attendants.
          </Text>
        </Col>
        <Col>
          <Space>
            <Button type="primary" icon={<SendOutlined />} onClick={() => onSendCampaign(true)} loading={sendMarketingEmailMutation.isPending}>
              Send Test Email
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Estimated Reach"
              value={estimatedReach}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Avg Open Rate" value={averageOpenRate} suffix="%" prefix={<MailOutlined />} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Avg CTR" value={averageCtr} suffix="%" prefix={<UserOutlined />} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Campaign Builder">
            <Form form={form} layout="vertical">
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item label="Campaign Name" name="campaignName" rules={[{ required: true, message: "Campaign name is required." }]}>
                    <Input placeholder="Example: May Retention Boost" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Template" required>
                    <Select options={templateOptions} placeholder="Select template" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Audience" required>
                <Radio.Group
                  value={recipientSegment}
                  onChange={(e) => setRecipientSegment(e.target.value)}
                >
                  <Space direction="vertical">
                    <Radio value="all">All Users and Attendants</Radio>
                    <Radio value="users">Users Only</Radio>
                    <Radio value="attendants">Attendants Only</Radio>
                    <Radio value="single">Single User</Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>

              {recipientSegment === "single" ? (
                <Form.Item
                  label="Recipient"
                  name="singleRecipientEmail"
                  rules={[{ required: true, message: "Select a recipient." }]}
                >
                  <Select
                    showSearch
                    placeholder="Search by name or email"
                    filterOption={false}
                    onSearch={onSearchRecipients}
                    loading={searchRecipientsMutation.isPending}
                    options={recipientOptions}
                    notFoundContent={searchRecipientsMutation.isPending ? "Searching..." : "Type at least 2 characters"}
                  />
                </Form.Item>
              ) : (
                <Form.Item label="Audience Filters" name="audienceFilters">
                  <Select
                    mode="multiple"
                    options={audienceFilterOptions}
                    placeholder="Filter by segments"
                    allowClear
                  />
                </Form.Item>
              )}

              <Form.Item label="Subject" name="subject" rules={[{ required: true, message: "Subject is required." }]}>
                <Input placeholder="Your campaign subject line" maxLength={120} showCount />
              </Form.Item>

              <Form.Item label="Email Body" name="body" rules={[{ required: true, message: "Email body is required." }]}>
                <ReactQuill
                  theme="snow"
                  placeholder="Write your campaign content..."
                  modules={quillModules}
                  formats={quillFormats}
                  className="custom-quill"
                />
              </Form.Item>

              <Form.Item
                label="Test Recipient Email"
                name="testRecipientEmail"
                rules={[
                  {
                    type: "email",
                    message: "Enter a valid test recipient email.",
                  },
                ]}
                extra="Used only when clicking Send Test Email."
              >
                <Input placeholder="you@example.com" />
              </Form.Item>

              <Form.Item label="Track Clicks">
                <Switch checked disabled />
              </Form.Item>

              <Space>
                <Button>Save draft</Button>
                <Button type="primary" icon={<SendOutlined />} onClick={() => onSendCampaign(false)} loading={sendMarketingEmailMutation.isPending}>
                  Send Campaign
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Live Preview">
            <Card size="small" style={{ background: "#fafafa" }}>
              <Text strong>Subject Preview</Text>
              <div style={{ marginTop: 8, marginBottom: 12 }}>
                <Text>{watchedSubject.trim() || "Enter a subject to preview"}</Text>
              </div>
              <Text strong>Body Preview</Text>
              <div style={{ marginTop: 8 }}>
                {(watchedBody || "").replace(/<[^>]*>/g, "").trim() ? (
                  <div dangerouslySetInnerHTML={{ __html: watchedBody }} />
                ) : (
                  <Text type="secondary">Enter email body content to preview</Text>
                )}
              </div>
            </Card>

            <div style={{ marginTop: 16 }}>
              <Text strong>Delivery Readiness</Text>
              <Progress percent={deliveryReadiness} strokeColor="#16a34a" />
            </div>
          </Card>
        </Col>
      </Row>

      <Card title="Recent Campaigns" style={{ marginTop: 16 }}>
        <Tabs
          items={[
            {
              key: "all",
              label: "All",
              children: <Table columns={columns} dataSource={campaigns} pagination={false} />,
            },
            {
              key: "users",
              label: "Users",
              children: (
                <Table
                  columns={columns}
                  dataSource={campaigns.filter((x) => x.segment.includes("User"))}
                  pagination={false}
                />
              ),
            },
            {
              key: "attendants",
              label: "Attendants",
              children: (
                <Table
                  columns={columns}
                  dataSource={campaigns.filter((x) => x.segment.includes("Attendant"))}
                  pagination={false}
                />
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default MarketingDashboard;
